import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT, IMAGES } from "../../data/content";
import { scrollToId, scrollToTop } from "../../hooks/useSmoothScroll";

const Logo = () => (
  <button
    data-testid="logo-home-btn"
    onClick={() => scrollToTop()}
    className="flex items-center group"
    aria-label="Tetőklinika kezdőlap"
  >
    <img src={IMAGES.logo} alt="Tetőklinika logó" className="h-9 sm:h-10 w-auto transition-transform group-hover:scale-[1.03]" />
  </button>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 60);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-10 h-16 md:h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.id)}
                className="text-sm font-medium text-slate-700 hover:text-emerald-600 transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-0.5 after:w-0 after:bg-[#0E5A75] after:transition-all hover:after:w-full"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={CONTACT.phoneHref}
              data-testid="header-phone-link"
              className="hidden md:flex items-center gap-2 text-sm font-semibold text-[#0E5A75] hover:text-emerald-600 transition-colors"
            >
              <Phone className="h-4 w-4" />
              {CONTACT.phoneDisplay}
            </a>
            <button
              data-testid="header-cta-btn"
              onClick={() => go("ajanlatkeres")}
              className="hidden sm:inline-flex items-center rounded-full bg-[#0E5A75] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 hover:bg-[#0E5A75] hover:scale-[1.04] transition-transform"
            >
              Ajánlatot kérek
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white/80 text-[#0E5A75]"
              aria-label="Menü megnyitása"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-[#0E5A75]/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              data-testid="mobile-menu-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-white p-6 flex flex-col"
            >
              <div className="flex items-center justify-between mb-8">
                <Logo />
                <button
                  data-testid="mobile-menu-close"
                  onClick={() => setOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-[#0E5A75]"
                  aria-label="Menü bezárása"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <button
                    key={l.id}
                    data-testid={`mobile-nav-${l.id}`}
                    onClick={() => go(l.id)}
                    className="text-left text-lg font-semibold text-[#0E5A75] py-3 border-b border-slate-100 hover:text-emerald-600 transition-colors"
                  >
                    {l.label}
                  </button>
                ))}
              </nav>
              <div className="mt-auto space-y-3 pt-6">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="mobile-phone-link"
                  className="flex items-center justify-center gap-2 rounded-full border border-[#0E5A75] py-3 font-semibold text-[#0E5A75]"
                >
                  <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                </a>
                <button
                  data-testid="mobile-cta-btn"
                  onClick={() => go("ajanlatkeres")}
                  className="w-full rounded-full bg-[#0E5A75] py-3 font-semibold text-white"
                >
                  Ajánlatot kérek
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
